import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";

export type TapInteractionOptions = {
  /** true 면 press 상태를 만들지 않고 핸들러도 동작하지 않는다 (disabled/loading). */
  disabled?: boolean;
  /**
   * 지정 시 pointerdown 시작점 대비 이동 거리(px)가 이 값을 넘으면 요소 안이어도 press 를 취소하고, 그 포인터 시퀀스에서
   * 파생되는 click 을 1회 억제한다 — 스크롤 리스트 안 카드용(Figma "누른 채 10px 초과 이동 시 탭 취소").
   * 미지정 = 영역 이탈 규칙만 적용.
   */
  cancelDistancePx?: number;
};

/**
 * 요소에 거는 핸들러는 둘뿐이다 — 이동·종료는 훅이 document 리스너로 추적한다.
 * 소비처는 이 묶음을 통째로 spread 한다(이름을 골라 붙이지 않는다).
 */
export type TapInteractionHandlers<T extends HTMLElement> = {
  onPointerDown: (event: ReactPointerEvent<T>) => void;
  /** 취소된 누름에서 파생된 click 을 캡처 단계에서 막는다(같은 엘리먼트 onClick 보다 먼저). */
  onClickCapture: (event: ReactMouseEvent<T>) => void;
};

/**
 * 취소된 누름의 click 억제 안전망 수명(ms). 합성 click 은 pointerup 직후 도착하지만(WebKit 터치는 별도 태스크일 수
 * 있음) 전통적 delayed click 최대 300ms 보다 길게 잡아 "click 이후" 를 보장하고, click 이 아예 안 생긴 경우
 * 억제가 다음 활성화를 먹지 않도록 지운다. 타이머는 포인터를 실제로 모두 뗀 뒤에야 시작한다.
 */
const SUPPRESS_CLICK_SAFETY_MS = 400;

/**
 * 터치·펜 손떨림 여유(px). 요소 밖으로 나갔더라도 누른 자리에서 이만큼 이내로만 움직였으면 탭으로 본다
 * (2026-10-23 결정 — 정의서 2차 행·카드의 "10px 초과 이동 시 탭 취소" 와 같은 값). 마우스는 여유 없음.
 */
const TOUCH_TAP_SLOP_PX = 10;

type PrimaryPointer = {
  pointerId: number;
  pointerType: string;
  startX: number;
  startY: number;
  /** pointerdown 순간의 요소 사각형 — 눌림 축소(scale)로 줄어든 사각형으로 판정하지 않도록 고정해 둔다. */
  rect: DOMRect | null;
  cancelDistancePx: number | undefined;
  canceled: boolean;
};

/**
 * 억제 방식. once = 취소된 누름의 click 1회만 막는다. all = 여러 포인터가 겹친 제스처라 포인터가 모두 떨어진 뒤
 * 안전망이 끝날 때까지 이 요소의 포인터 click 을 모두 막는다(어느 포인터에서 click 이 날지 브라우저마다 달라 개수로 세지 않는다).
 */
type SuppressMode = "none" | "once" | "all";

const isPointInRect = (rect: DOMRect, x: number, y: number): boolean =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/** 이 좌표에서 누름을 취소해야 하는가 — 이동 거리 옵션 → 영역 이탈(마우스 즉시 / 터치·펜 10px 여유) 순. */
const shouldCancelAt = (primary: PrimaryPointer, x: number, y: number): boolean => {
  const moved = Math.hypot(x - primary.startX, y - primary.startY);
  if (primary.cancelDistancePx !== undefined && moved > primary.cancelDistancePx) return true;
  if (!primary.rect || isPointInRect(primary.rect, x, y)) return false;
  if (primary.pointerType === "mouse") return true;
  return moved > TOUCH_TAP_SLOP_PX;
};

/**
 * 공통 press 인터랙션 생명주기 훅.
 *
 * 책임은 "pointer press/release/cancel 상태(isPressed)" 관리와 취소된 누름의 click 억제뿐이며, transform 이나 color 를
 * 직접 강제하지 않는다. 실제 시각 효과는 각 컴포넌트의 CSS Module 이 isPressed 클래스로 적용한다.
 *
 * 동작 규약:
 * - disabled: press 상태를 만들지 않고 핸들러도 no-op. 누르는 도중 disabled 가 되면 그 누름은 취소로 본다(실제로 뗄 때까지
 *   추적해, 떼기 전에 다시 활성화돼도 그 누름의 click 은 막는다).
 * - action 실행은 element 의 native onClick 에 맡긴다(실행 시점 = 뗄 때 즉시). 키보드(Enter/Space) 접근성이 보존된다.
 * - 추적: pointerdown 으로 시작한 포인터(pointerId)를 document 캡처 단계 리스너로 실제 pointerup/pointercancel 까지 따라간다
 *   (요소 포인터 캡처는 쓰지 않는다). 창 blur·문서 숨김·마우스 buttons 0(이미 뗌)도 종료로 본다. 앞 누름이 마우스인데 새 마우스
 *   down 이 오거나 같은 pointerId 의 down 이 오면 앞 누름은 이미 끝난 것으로 보고 새로 시작한다.
 * - 영역 이탈 = 취소: 기준은 pointerdown 순간의 요소 사각형. 마우스는 그 밖이면, 터치·펜은 그 밖이고 누른 자리에서 10px 넘게
 *   움직였으면 그 누름을 끝낸다 — 눌림이 풀리고, 다시 들어와도 되살아나지 않으며, 그 누름에서 나오는 click 1회를 막는다.
 *   판정은 매 이동과 손을 뗀 최종 좌표 둘 다에서 한다(이동 이벤트 없이 밖에서 떼도 취소).
 * - 여러 포인터: 누르는 동안 같은 요소에 다른 포인터(두 번째 손가락·터치 중 마우스)가 닿으면 제스처로 보고 그 누름을 취소한다.
 *   그 뒤 모든 포인터가 떨어지고 안전망이 끝날 때까지 이 요소의 포인터 click 을 모두 막는다.
 * - cancelDistancePx(opt-in): 시작점 대비 이동이 그 값을 넘으면 요소 안이어도 같은 방식으로 취소한다.
 * - click 억제 수명: (한 포인터) 취소된 누름의 click 1회 소비 / (여러 포인터) 모든 포인터를 뗀 뒤 400ms 까지 전부 —
 *   어느 쪽이든 새 누름 시작이나 포인터를 모두 뗀 뒤 400ms 안전망에서 끝난다. 누른 채 밖에 오래 머무는 동안에는 꺼지지 않는다.
 * - 키보드 활성화는 포인터 억제와 별개로 항상 통과한다: 억제 중에만 document 키 리스너를 붙여, 이 요소에서 click 을 내는
 *   그 키 이벤트(Enter keydown — 반복 포함, Space keyup)에서만 1회용 표시를 켠다. 표시는 그 click 을 통과시키며 꺼지고,
 *   포인터 억제는 그대로 남는다. 다른 키(Shift·Tab 등)는 영향 없음.
 * - 한계: 키 이벤트 없이 click 만 보내는 보조기기 활성화가 취소된 누름이 끝난 직후 400ms 안에 겹치면 무시될 수 있다.
 *   마우스로 가장자리 2% 띠 안에서 누르고 축소된 상자 밖(원래 사각형 안)에서 떼면 브라우저가 click 을 부모로 보낼 수 있다.
 *
 * @typeParam T - press 핸들러와 ref 를 붙일 엘리먼트 타입 (예: HTMLButtonElement)
 */
export const useTapInteraction = <T extends HTMLElement = HTMLElement>({
  disabled = false,
  cancelDistancePx,
}: TapInteractionOptions = {}) => {
  const elementRef = useRef<T>(null);
  const [isPressed, setIsPressed] = useState(false);

  // 지금 진행 중인 제스처 — 처음 누른 포인터(primary)와, 그 뒤 겹쳐 닿은 다른 포인터들(extra).
  const primaryRef = useRef<PrimaryPointer | null>(null);
  const extraPointersRef = useRef(new Map<number, string>()); // pointerId → pointerType
  const detachGestureRef = useRef<(() => void) | null>(null);

  const suppressRef = useRef<SuppressMode>("none");
  const safetyTimerRef = useRef<number>(0);

  const keyboardActivationRef = useRef(false);
  const keyboardResetTimerRef = useRef<number>(0);
  const detachKeysRef = useRef<(() => void) | null>(null);

  const clearSafetyTimer = useCallback(() => {
    if (safetyTimerRef.current) {
      window.clearTimeout(safetyTimerRef.current);
      safetyTimerRef.current = 0;
    }
  }, []);

  const clearKeyboardMark = useCallback(() => {
    keyboardActivationRef.current = false;
    if (keyboardResetTimerRef.current) {
      window.clearTimeout(keyboardResetTimerRef.current);
      keyboardResetTimerRef.current = 0;
    }
  }, []);

  const detachKeys = useCallback(() => {
    detachKeysRef.current?.();
    detachKeysRef.current = null;
    clearKeyboardMark();
  }, [clearKeyboardMark]);

  const clearSuppression = useCallback(() => {
    suppressRef.current = "none";
    clearSafetyTimer();
    detachKeys();
  }, [clearSafetyTimer, detachKeys]);

  const armSafetyClear = useCallback(() => {
    clearSafetyTimer();
    safetyTimerRef.current = window.setTimeout(() => {
      safetyTimerRef.current = 0;
      suppressRef.current = "none";
      detachKeys();
    }, SUPPRESS_CLICK_SAFETY_MS);
  }, [clearSafetyTimer, detachKeys]);

  /** 이 요소에서 키보드 click 이 나는 키 이벤트에만 1회용 표시를 켠다(click 이 안 오면 다음 태스크에 꺼짐). */
  const markKeyboardActivation = useCallback(() => {
    keyboardActivationRef.current = true;
    if (keyboardResetTimerRef.current) window.clearTimeout(keyboardResetTimerRef.current);
    keyboardResetTimerRef.current = window.setTimeout(() => {
      keyboardActivationRef.current = false;
      keyboardResetTimerRef.current = 0;
    }, 0);
  }, []);

  const turnOnSuppression = useCallback(
    (mode: "once" | "all") => {
      // all 은 once 보다 강하다 — 한 번 all 이 되면 안전망이 끝날 때까지 all 로 둔다.
      if (suppressRef.current !== "all") suppressRef.current = mode;
      clearSafetyTimer();
      if (detachKeysRef.current) return;

      const isOwnTarget = (target: EventTarget | null) => {
        const element = elementRef.current;
        return element !== null && target instanceof Node && element.contains(target);
      };
      // Enter 는 keydown 의 기본 동작으로 click 을 낸다(누르고 있으면 반복 keydown 마다).
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && isOwnTarget(event.target)) markKeyboardActivation();
      };
      // Space 는 keyup 의 기본 동작으로 click 을 낸다 — 누르고 있는 동안에는 표시하지 않는다.
      const handleKeyUp = (event: KeyboardEvent) => {
        if ((event.key === " " || event.key === "Spacebar") && isOwnTarget(event.target)) {
          markKeyboardActivation();
        }
      };
      document.addEventListener("keydown", handleKeyDown, true);
      document.addEventListener("keyup", handleKeyUp, true);
      detachKeysRef.current = () => {
        document.removeEventListener("keydown", handleKeyDown, true);
        document.removeEventListener("keyup", handleKeyUp, true);
      };
    },
    [clearSafetyTimer, markKeyboardActivation],
  );

  const cancelPrimary = useCallback(
    (primary: PrimaryPointer) => {
      if (primary.canceled) return;
      primary.canceled = true;
      turnOnSuppression("once");
      setIsPressed(false);
    },
    [turnOnSuppression],
  );

  /** 제스처를 모두 끝낸다 — 리스너 제거·눌림 해제. 억제가 켜져 있으면 안전망을 시작한다. */
  const endGesture = useCallback(() => {
    // 진행 중인 제스처가 없으면(리스너도 없고 추적 중인 포인터도 없음) 할 일이 없다.
    if (!detachGestureRef.current && !primaryRef.current && extraPointersRef.current.size === 0) return;
    primaryRef.current = null;
    extraPointersRef.current.clear();
    detachGestureRef.current?.();
    detachGestureRef.current = null;
    setIsPressed(false);
    if (suppressRef.current !== "none") armSafetyClear();
  }, [armSafetyClear]);

  /** 포인터 하나가 끝났다 — 남은 포인터가 없으면 제스처를 끝낸다. */
  const releasePointer = useCallback(
    (pointerId: number) => {
      const primary = primaryRef.current;
      if (primary && primary.pointerId === pointerId) {
        primaryRef.current = null;
        setIsPressed(false);
      } else {
        extraPointersRef.current.delete(pointerId);
      }
      if (!primaryRef.current && extraPointersRef.current.size === 0) endGesture();
    },
    [endGesture],
  );

  const attachGestureListeners = useCallback(() => {
    if (detachGestureRef.current) return;
    const isTracked = (pointerId: number) =>
      primaryRef.current?.pointerId === pointerId || extraPointersRef.current.has(pointerId);

    const handleUp = (event: PointerEvent) => {
      if (!isTracked(event.pointerId)) return;
      const primary = primaryRef.current;
      // 마지막 이동 없이 밖에서 떼는 경로까지 최종 좌표로 판정한다.
      if (primary && primary.pointerId === event.pointerId && shouldCancelAt(primary, event.clientX, event.clientY)) {
        cancelPrimary(primary);
      }
      releasePointer(event.pointerId);
    };
    const handleMove = (event: PointerEvent) => {
      // 마우스 버튼이 이미 떨어졌는데 up 을 못 받은 경우(창 밖에서 뗌 등) — 이 좌표에서 뗀 것으로 본다(겹친 마우스도 같다).
      if (event.pointerType === "mouse" && event.buttons === 0 && isTracked(event.pointerId)) {
        handleUp(event);
        return;
      }
      const primary = primaryRef.current;
      if (!primary || primary.pointerId !== event.pointerId) return;
      if (!primary.canceled && shouldCancelAt(primary, event.clientX, event.clientY)) cancelPrimary(primary);
    };
    const handleCancel = (event: PointerEvent) => {
      if (isTracked(event.pointerId)) releasePointer(event.pointerId);
    };
    const handleBlur = () => endGesture();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") endGesture();
    };

    document.addEventListener("pointermove", handleMove, { capture: true, passive: true });
    document.addEventListener("pointerup", handleUp, true);
    document.addEventListener("pointercancel", handleCancel, true);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    detachGestureRef.current = () => {
      document.removeEventListener("pointermove", handleMove, true);
      document.removeEventListener("pointerup", handleUp, true);
      document.removeEventListener("pointercancel", handleCancel, true);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cancelPrimary, releasePointer, endGesture]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<T>) => {
      if (disabled) return;
      // 마우스 보조 버튼은 무시한다 (primary=0, touch/pen=0).
      if (event.button > 0) return;

      // 종료 이벤트를 놓친 기록을 뺀다 — 마우스는 하나뿐이라 새 마우스 down 이면 남은 마우스 기록은 끝난 것이고,
      // 같은 pointerId 의 down 이면 그 id 의 앞 기록도 끝난 것이다. 아직 눌린 다른 포인터(터치 등)는 그대로 둔다.
      const extras = extraPointersRef.current;
      const isStaleRecord = (pointerId: number, pointerType: string) =>
        pointerId === event.pointerId || (event.pointerType === "mouse" && pointerType === "mouse");
      const stalePrimary = primaryRef.current;
      if (stalePrimary && isStaleRecord(stalePrimary.pointerId, stalePrimary.pointerType)) {
        primaryRef.current = null;
        setIsPressed(false);
      }
      for (const [pointerId, pointerType] of [...extras]) {
        if (isStaleRecord(pointerId, pointerType)) extras.delete(pointerId);
      }
      // 기록을 빼고 나니 남은 포인터가 없으면 앞 제스처를 끝낸다(남아 있으면 이 down 은 겹친 포인터로 이어진다).
      if (!primaryRef.current && extras.size === 0) endGesture();

      if (primaryRef.current || extraPointersRef.current.size > 0) {
        // 누르는 동안 다른 포인터가 닿았다 — 제스처로 보고 누름을 취소, 포인터를 모두 뗄 때까지 click 을 전부 막는다.
        extraPointersRef.current.set(event.pointerId, event.pointerType);
        if (primaryRef.current) cancelPrimary(primaryRef.current);
        turnOnSuppression("all");
        return;
      }

      // 새 누름 — 이전 억제는 리셋한다.
      clearSuppression();
      primaryRef.current = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        rect: elementRef.current ? elementRef.current.getBoundingClientRect() : null,
        cancelDistancePx,
        canceled: false,
      };
      attachGestureListeners();
      setIsPressed(true);
    },
    [disabled, cancelDistancePx, endGesture, cancelPrimary, turnOnSuppression, clearSuppression, attachGestureListeners],
  );

  const onClickCapture = useCallback(
    (event: ReactMouseEvent<T>) => {
      // 키보드 활성화 click — 통과시키고 표시만 끈다(포인터 억제는 유지).
      if (keyboardActivationRef.current) {
        clearKeyboardMark();
        return;
      }
      const mode = suppressRef.current;
      if (mode === "none") return;
      // once 는 그 click 하나로 끝, all 은 안전망이 끝날 때까지 유지.
      if (mode === "once") clearSuppression();
      event.preventDefault();
      event.stopPropagation();
    },
    [clearKeyboardMark, clearSuppression],
  );

  // 누르는 도중 disabled 가 되면 그 누름은 취소로 본다 — 실제로 뗄 때까지 추적해, 떼기 전에 다시 활성화되더라도
  // 그 누름의 click 은 막는다(포인터를 모두 떼면 endGesture 가 안전망 시작).
  useEffect(() => {
    const primary = primaryRef.current;
    if (disabled && primary) cancelPrimary(primary);
  }, [disabled, cancelPrimary]);

  // 언마운트 — 붙어 있는 리스너·타이머 정리.
  useEffect(
    () => () => {
      primaryRef.current = null;
      extraPointersRef.current.clear();
      detachGestureRef.current?.();
      detachGestureRef.current = null;
      suppressRef.current = "none";
      clearSafetyTimer();
      detachKeys();
    },
    [clearSafetyTimer, detachKeys],
  );

  return {
    isPressed: disabled ? false : isPressed,
    ref: elementRef,
    pressHandlers: {
      onPointerDown,
      onClickCapture,
    } satisfies TapInteractionHandlers<T>,
  };
};
